import React, { createContext, useContext, useState, useEffect } from "react";
import API from "../config/api.js";
import AppLoadingScreen from "../components/common/AppLoadingScreen.jsx";

/**
 * Contexte de gestion des langues
 * Toutes les traductions sont chargées depuis la base de données (API)
 * Pas de traductions stockées dans le frontend
 */

const DEFAULT_LANGUAGES = [
  { code: "fr", name: "Français", flag: "🇫🇷", nativeName: "Français" },
  { code: "en", name: "English", flag: "🇬🇧", nativeName: "English" },
  { code: "km", name: "Khmer", flag: "🇰🇭", nativeName: "ភាសាខ្មែរ" },
];

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem("language") || "fr";
  });
  const [apiTranslations, setApiTranslations] = useState({});
  const [apiDictionaries, setApiDictionaries] = useState({});
  const [languages, setLanguages] = useState(DEFAULT_LANGUAGES);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    localStorage.setItem("language", language);
  }, [language]);

  // Charger la liste des langues depuis l'API (en arrière-plan, n'affecte pas isLoading)
  useEffect(() => {
    fetch(`${API.baseURL}/languages`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.data?.length > 0) {
          setLanguages(
            data.data.map((l) => ({
              code: l.code,
              name: l.name,
              nativeName: l.native_name || l.name,
              flag: l.flag_emoji || "🌐",
            })),
          );
        }
      })
      .catch(() => {});
  }, []);

  // Charger les traductions pour toutes les langues au montage (pour fallback km → fr/en)
  useEffect(() => {
    const langs = ["fr", "en", "km"];
    setIsLoading(true);
    Promise.all(
      langs.map((lang) =>
        fetch(`${API.baseURL}/languages/all/${lang}`)
          .then((res) => res.json())
          .then((data) =>
            data.success && data.data ? { lang, data: data.data } : null,
          )
          .catch(() => null),
      ),
    )
      .then((results) => {
        const next = {};
        const nextDict = {};
        results.forEach((r) => {
          if (r) {
            next[r.lang] = r.data.translations || {};
            nextDict[r.lang] = r.data.dictionaries || {};
          }
        });
        setApiTranslations(next);
        setApiDictionaries(nextDict);
      })
      .finally(() => setIsLoading(false));
  }, []);

  const t = (key) => {
    const val = apiTranslations[language]?.[key];
    if (val) return val;
    return apiTranslations.fr?.[key] || apiTranslations.en?.[key] || key;
  };

  const translateRecipeName = (name) => {
    const dict = apiDictionaries[language]?.recipe;
    return (dict && dict[name]) || name;
  };

  const translateIngredient = (ingredient) => {
    const dict = apiDictionaries[language]?.ingredient;
    return (dict && dict[ingredient]) || ingredient;
  };

  const translateCategory = (category) => {
    if (!category || typeof category !== "string") return category || "";
    const dict = apiDictionaries[language]?.category;
    return (dict && dict[category]) || category;
  };

  const translateInstructions = (instructions) => {
    if (!instructions || language !== "km") return instructions;
    const dict = apiDictionaries[language]?.instruction;
    if (!dict || typeof dict !== "object") return instructions;

    let translated = instructions;
    const sortedKeys = Object.keys(dict).sort((a, b) => b.length - a.length);
    sortedKeys.forEach((fr) => {
      const regex = new RegExp(fr.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi");
      translated = translated.replace(regex, dict[fr] || fr);
    });
    return translated;
  };

  const changeLanguage = (lang) => {
    setLanguage(lang);
    const token = localStorage.getItem("token");
    if (token && ["fr", "en", "km"].includes(lang)) {
      fetch(`${API.baseURL}/users/profile/preferred-language`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ preferred_language: lang }),
      }).catch(() => {});
    }
  };

  if (isLoading) {
    return (
      <AppLoadingScreen
        message="Chargement..."
        subMessage="Préparation des traductions"
      />
    );
  }

  return (
    <LanguageContext.Provider
      value={{
        language,
        changeLanguage,
        t,
        translateRecipeName,
        translateIngredient,
        translateCategory,
        translateInstructions,
        languages,
        isLoading: false,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
};

export default LanguageContext;
